import type { AssetMap } from "../assets/loader";

/**
 * Compact Mario / South Park figure drawn as ONE connected sprite.
 * - run.png  → grounded
 * - jump.png → airborne (legs out)
 */
export const PLAYER_DRAW = {
  /** On-screen draw size — large mascot, face readable */
  w: 104,
  h: 208,
} as const;

export class Player {
  x = 200;
  y = 0;
  vy = 0;
  w = 48;
  h = 96;
  grounded = false;
  ducking = false;
  alive = true;

  private coyote = 0;
  private jumpBuffer = 0;
  private runPhase = 0;
  private squash = 1;
  /** True after variable-jump cut applied once this airtime */
  private jumpCut = false;
  /** Double jump power-up state */
  hasDoubleJump = false;
  private doubleJumpUsed = false;
  private readonly gravity = 2400;
  private readonly jumpVel = -1240;
  /** Release clamp — short hop, applied once (never multiply every frame) */
  private readonly jumpReleaseClamp = -440;
  private readonly assets: AssetMap;

  constructor(assets: AssetMap, groundY: number) {
    this.assets = assets;
    this.y = groundY;
  }

  get hitbox(): { x: number; y: number; w: number; h: number } {
    if (this.ducking && this.grounded) {
      return { x: this.x - 18, y: this.y - 40, w: 36, h: 40 };
    }
    // Body hitbox (ignore overhead bucket fluff)
    return { x: this.x - 18, y: this.y - 72, w: 36, h: 66 };
  }

  reset(groundY: number): void {
    this.x = 200;
    this.y = groundY;
    this.vy = 0;
    this.grounded = true;
    this.ducking = false;
    this.alive = true;
    this.coyote = 0;
    this.jumpBuffer = 0;
    this.runPhase = 0;
    this.squash = 1;
    this.jumpCut = false;
    this.hasDoubleJump = false;
    this.doubleJumpUsed = false;
  }

  requestJump(): void {
    this.jumpBuffer = 0.16;
  }

  update(
    dt: number,
    duckHeld: boolean,
    jumpHeld: boolean,
    groundY: number,
    onJump?: () => void
  ): void {
    if (!this.alive) return;

    this.ducking = duckHeld && this.grounded;
    this.jumpBuffer = Math.max(0, this.jumpBuffer - dt);

    if (this.grounded) this.coyote = 0.12;
    else this.coyote = Math.max(0, this.coyote - dt);

    // Ducking blocks jump only while still holding duck on the ground
    const canJump = (this.grounded || this.coyote > 0) && !this.ducking;
    if (this.jumpBuffer > 0 && canJump) {
      this.vy = this.jumpVel;
      this.grounded = false;
      this.coyote = 0;
      this.jumpBuffer = 0;
      this.jumpCut = false;
      this.doubleJumpUsed = false;
      this.squash = 1.06;
      onJump?.();
    } else if (this.jumpBuffer > 0 && this.hasDoubleJump && !this.grounded && !this.doubleJumpUsed) {
      this.vy = this.jumpVel * 0.85;
      this.jumpBuffer = 0;
      this.jumpCut = false;
      this.doubleJumpUsed = true;
      this.squash = 1.04;
      onJump?.();
    }

    // Variable jump height: clamp once on release (NOT every frame)
    if (!jumpHeld && !this.jumpCut && this.vy < this.jumpReleaseClamp) {
      this.vy = this.jumpReleaseClamp;
      this.jumpCut = true;
    }

    this.vy += this.gravity * dt;
    this.y += this.vy * dt;

    if (this.y >= groundY) {
      if (this.vy > 600) this.squash = 0.92;
      this.y = groundY;
      this.vy = 0;
      this.grounded = true;
      this.jumpCut = false;
      this.doubleJumpUsed = false;
    } else {
      this.grounded = false;
    }

    this.runPhase += dt * (this.grounded && !this.ducking ? 14 : 5);
    this.squash += (1 - this.squash) * Math.min(1, dt * 14);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const bob =
      this.grounded && !this.ducking ? Math.sin(this.runPhase) * 2 : 0;
    const sprite = this.grounded
      ? this.assets["player.run"]
      : this.assets["player.jump"];

    // Keep on-screen size consistent so jump never "shrinks" the mascot
    let dw: number = PLAYER_DRAW.w;
    let dh: number = PLAYER_DRAW.h;
    if (!this.grounded) {
      dw = 130;
      dh = PLAYER_DRAW.h;
    }

    ctx.save();
    ctx.translate(this.x, this.y + bob);
    ctx.scale(1, this.squash);
    if (this.ducking && this.grounded) {
      ctx.scale(1.1, 0.72);
      ctx.translate(0, 6);
    }

    if (this.grounded && !this.ducking) {
      ctx.rotate(Math.sin(this.runPhase) * 0.03);
    }

    ctx.drawImage(sprite, -dw / 2, -dh, dw, dh);
    ctx.restore();
  }
}
